import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { SectionAccordionComponent } from '../../../../../shared/components/section-accordion';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { HeroLayout1PreviewComponent } from './layouts/layout_1';
import { HeroLayout2PreviewComponent } from './layouts/layout_2';
import { HeroLayout3PreviewComponent } from './layouts/layout_3';
import { HeroLayout4PreviewComponent } from './layouts/layout_4';
import { HeroLayout5PreviewComponent } from './layouts/layout_5';
import { HeroLayout6PreviewComponent } from './layouts/layout_6';
import { HeroLayout7PreviewComponent } from './layouts/layout_7';
import { TemplateService } from '../../../../../car-wash-website/services/template.service';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;

export interface HeroImage {
  src?: string;
  alt?: string;
  width?: string;
  height?: string;
  scaletype?: 'fill' | 'fit' | 'stretch';
}

export interface HeroPanelData {
  heading: string;
  paragraph: string;
  titleStyle?: TextEditorStyle;
  image?: HeroImage;
  // Backward compatibility properties for dark/modern themes
  imageUrl?: string;
  imageFit: 'fill' | 'fit' | 'stretch'; // Required for compatibility with dark/modern themes
  buttonText: string;
  buttonNavigation: 'homepage' | 'express-washes' | 'express-detail' | 'fleets' | 'coupons';
  backgroundColor: string;
  layout?:
    | 'layout_1'
    | 'layout_2'
    | 'layout_3'
    | 'layout_4'
    | 'layout_5'
    | 'layout_6'
    | 'layout_7'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'; // Hero section layout: layout_1=text left/image right, layout_2=centered with image top, layout_3=image left/text right, layout_4=text top with bg image, layout_5=text centered with bg image, layout_6=text bottom with bg image, layout_7=modern template style with full background
}

type TabType = 'content' | 'layout';
type SectionType = 'heading' | 'media' | 'button' | 'background';

@Component({
  selector: 'app-minimal-hero-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ColorPickerModule,
    SectionAccordionComponent,
    TextEditorComponent,
    HeroLayout1PreviewComponent,
    HeroLayout2PreviewComponent,
    HeroLayout3PreviewComponent,
    HeroLayout4PreviewComponent,
    HeroLayout5PreviewComponent,
    HeroLayout6PreviewComponent,
    HeroLayout7PreviewComponent,
  ],
  templateUrl: './hero-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './hero-panel.component.scss',
})
export class MinimalHeroPanelComponent implements OnChanges, OnInit {
  @Input() data: HeroPanelData = this.getDefaultData();
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<HeroPanelData>();
  @Output() valueChange = new EventEmitter<HeroPanelData>();

  activeTab: TabType = 'content';
  expandedSection: SectionType | null = 'heading';
  customColors: string[] = [];

  // Color picker state (same as dark theme)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  // Dropdown states
  showImageFitDropdown = false;
  showNavigationDropdown = false;
  imageFitDropdownAbove = false;
  navigationDropdownAbove = false;

  // Image upload state
  isUploadingImage = false;
  uploadError: string | null = null;

  private static readonly DEFAULT_DATA: HeroPanelData = {
    heading: '',
    paragraph: '',
    image: {
      src: '',
      alt: '',
      width: '420px',
      height: '320px',
      scaletype: 'fit',
    },
    imageFit: 'fit', // Required for compatibility with dark/modern themes
    buttonText: '',
    buttonNavigation: 'express-washes',
    backgroundColor: '#1B5BFF',
    layout: '1', // Default to layout 1
  };

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(
    private logger: LoggerService,
    private templateService: TemplateService,
    private messageService: NotificationService
  ) {}

  // Lifecycle hook - called on component initialization
  ngOnInit(): void {
    this.logger.debug('🟣 HeroPanel ngOnInit - initial data:', {
      heading: this.data?.heading,
      paragraph: this.data?.paragraph,
      headingLength: this.data?.heading?.length,
      paragraphLength: this.data?.paragraph?.length,
    });
    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  // Lifecycle hook to detect when data input changes
  /**
   * Handle Angular OnChanges lifecycle for the hero panel: logs meaningful info when the `data` input changes and triggers loading of a custom background color if provided.
   * @example
   * gOnChanges({ data: { previousValue: { heading: 'Old', paragraph: 'Old text' }, currentValue: { heading: 'New', paragraph: 'New text' } } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Object containing changed @Input() properties keyed by property name.
   * @returns {{void}} No return value; performs logging and may call loadCustomColorFromData() as a side effect.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.logger.debug('🟣 HeroPanel ngOnChanges - data changed:', {
        heading: this.data?.heading,
        paragraph: this.data?.paragraph,
        headingLength: this.data?.heading?.length,
        paragraphLength: this.data?.paragraph?.length,
        previousHeading: changes['data'].previousValue?.heading,
        currentHeading: changes['data'].currentValue?.heading,
      });
    }
    if (changes['data'] && this.data?.backgroundColor) {
      // Load custom color if backgroundColor is not in predefined colors
      this.loadCustomColorFromData();
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Load a custom background color from the component's data and update this.customColors accordingly (at most one custom color).
   * @example
   * loadCustomColorFromData()
   * // side-effect: this.customColors will be set to [] if the background color is predefined, or [bgColor] if it's custom
   * @param {void} none - This method takes no arguments.
   * @returns {void} No return value; updates the component's customColors as a side effect.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!MinimalHeroPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  // Public methods
  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    this.save.emit(this.data);
  }

  onTabChange(tab: TabType): void {
    this.activeTab = tab;
  }

  onHeadingChange(value: string): void {
    this.logger.debug('🟣 HeroPanel onHeadingChange', { value, length: value?.length });
    this.data.heading = value;
    this.logger.debug('🟣 HeroPanel emitting valueChange with heading:', this.data.heading);
    this.valueChange.emit(this.data);
  }

  onHeadingStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.valueChange.emit(this.data);
  }

  onParagraphChange(value: string): void {
    this.logger.debug('🟣 HeroPanel onParagraphChange', { value, length: value?.length });
    this.data.paragraph = value;
    this.logger.debug('🟣 HeroPanel emitting valueChange with paragraph:', this.data.paragraph);
    this.valueChange.emit(this.data);
  }

  onReplaceImage(): void {
    this.createFileInput();
  }

  onImageFitChange(value: 'fill' | 'fit' | 'stretch'): void {
    if (!this.data.image) {
      this.data.image = { scaletype: value };
    } else {
      this.data.image.scaletype = value;
    }
    // Update backward compatibility property
    this.data.imageFit = value;
    this.showImageFitDropdown = false;
    this.valueChange.emit(this.data);
  }

  toggleImageFitDropdown(): void {
    this.showImageFitDropdown = !this.showImageFitDropdown;
    if (this.showImageFitDropdown) {
      this.showNavigationDropdown = false;
      // Check if dropdown should appear above
      this.checkImageFitDropdownPosition();
    } else {
      this.imageFitDropdownAbove = false;
    }
  }

  /**
   * Determine whether the image fit dropdown should open above its trigger based on available viewport space and update this.imageFitDropdownAbove accordingly.
   * @example
   * checkImageFitDropdownPosition()
   * undefined
   * @returns {{void}} Updates component state to indicate if the dropdown should display above the button.
   **/
  private checkImageFitDropdownPosition(): void {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      const dropdownButton = document.querySelector('.image-fit-dropdown-button') as HTMLElement;
      const dropdownMenu = document.querySelector('.image-fit-dropdown-menu') as HTMLElement;

      if (dropdownButton && dropdownMenu) {
        const buttonRect = dropdownButton.getBoundingClientRect();
        const menuHeight = dropdownMenu.offsetHeight || 180; // Approximate height for 4 options
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const gap = 4; // Gap between button and menu

        // If not enough space below but enough space above, show above
        this.imageFitDropdownAbove = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow;
      }
    });
  }

  getImageFitLabel(): string {
    const scaletype = this.data.image?.scaletype || 'fit';
    return scaletype.charAt(0).toUpperCase() + scaletype.slice(1);
  }

  onButtonTextChange(value: string): void {
    this.data.buttonText = value;
    this.valueChange.emit(this.data);
  }

  onButtonNavigationChange(
    value: 'homepage' | 'express-washes' | 'express-detail' | 'fleets' | 'coupons'
  ): void {
    this.data.buttonNavigation = value;
    this.showNavigationDropdown = false;
    this.valueChange.emit(this.data);
  }

  toggleNavigationDropdown(): void {
    this.showNavigationDropdown = !this.showNavigationDropdown;
    if (this.showNavigationDropdown) {
      this.showImageFitDropdown = false;
      // Check if dropdown should appear above
      this.checkNavigationDropdownPosition();
    } else {
      this.navigationDropdownAbove = false;
    }
  }

  /**
   * Check whether the navigation dropdown should be positioned above the toggle based on available viewport space and update component state.
   * @example
   * checkNavigationDropdownPosition()
   * undefined
   * @returns {void} Updates this.navigationDropdownAbove to true when there is insufficient space below the button and more space above; otherwise leaves it false.
   **/
  private checkNavigationDropdownPosition(): void {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      const dropdownButton = document.querySelector('.navigation-dropdown-button') as HTMLElement;
      const dropdownMenu = document.querySelector('.navigation-dropdown-menu') as HTMLElement;

      if (dropdownButton && dropdownMenu) {
        const buttonRect = dropdownButton.getBoundingClientRect();
        const menuHeight = dropdownMenu.offsetHeight || 280; // Approximate height for 5 options
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        const gap = 4; // Gap between button and menu

        // If not enough space below but enough space above, show above
        this.navigationDropdownAbove = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow;
      }
    });
  }

  getNavigationLabel(): string {
    const labels: Record<string, string> = {
      homepage: 'Homepage',
      'express-washes': 'Express Washes',
      'express-detail': 'Express Detail',
      fleets: 'Fleets',
      coupons: 'Coupons',
    };
    return labels[this.data.buttonNavigation] || 'Homepage';
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.valueChange.emit(this.data);
  }

  onLayoutChange(layout: '1' | '2' | '3' | '4' | '5' | '6' | '7'): void {
    // Convert to layout_X format
    const layoutType = `layout_${layout}` as
      'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
    this.data.layout = layoutType;
    this.valueChange.emit(this.data);
  }

  /**
   * Get the currently selected hero layout as a string between '1' and '7', defaulting to '1' if unspecified or invalid.
   * @example
   * etSelectedLayout()
   * '1'
   * @returns {{'1' | '2' | '3' | '4' | '5' | '6' | '7'}} Selected layout identifier as a string.
   **/
  getSelectedLayout(): '1' | '2' | '3' | '4' | '5' | '6' | '7' {
    const layout = this.data.layout;
    if (!layout) return '1';

    // If it's already in layout_X format, extract the number
    if (typeof layout === 'string' && layout.startsWith('layout_')) {
      const num = layout.replace('layout_', '');
      if (/^[1-7]$/.test(num)) {
        return num as '1' | '2' | '3' | '4' | '5' | '6' | '7';
      }
    }

    // If it's already a number string, return it
    if (typeof layout === 'string' && /^[1-7]$/.test(layout)) {
      return layout as '1' | '2' | '3' | '4' | '5' | '6' | '7';
    }

    return '1';
  }

  /**
   * Convert HeroPanelData to HeroLayout1Data format for the preview component
   */
  getHeroLayout1Data() {
    return {
      title: this.data?.heading,
      subtitle: undefined,
      description: this.data?.paragraph,
      buttonText: this.data?.buttonText,
      buttonLink: this.data?.buttonNavigation,
      image: this.data?.image,
      imageUrl: this.data?.imageUrl,
      backgroundColor: this.data?.backgroundColor,
      titleStyle: this.data?.titleStyle,
    };
  }

  /**
   * Get heading color with fallback
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color with fallback
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#555963';
  }

  /**
   * Get primary color with fallback
   */
  getPrimaryColor(): string {
    return this.primaryColor || this.data?.backgroundColor || '#1B5BFF';
  }

  /**
   * Get heading font with fallback
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope, sans-serif';
  }

  /**
   * Get body font with fallback
   */
  getBodyFont(): string {
    return this.bodyFont || 'Manrope, sans-serif';
  }

  onAddColor(): void {
    // Open color picker modal (same as dark theme)
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Finalize and close the color picker: normalize and apply the selected color, add it to custom colors (replacing any existing custom color), and close the picker.
   * @example
   * loseColorPicker()
   * undefined
   * @returns {void} No return value.
   */
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize and apply the selected color from the color picker: extracts a hex string
   * from the picker value (supports a hex string or an object with a hex property),
   * ensures it starts with '#', updates this.currentPickerColor with the normalized hex,
   * and calls onBackgroundColorChange(hex) to preview the background color without saving it.
   * @example
   * this.nColorPickerChange()
   * // returns void
   * @returns {void} Does not return a value.
   */
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  toggleSection(section: SectionType): void {
    const wasExpanded = this.expandedSection === section;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSection = null;
    } else {
      // If clicking a collapsed section, expand this one
      this.expandedSection = section;
    }
  }

  isSectionExpanded(section: SectionType): boolean {
    return this.expandedSection === section;
  }

  // Private helper methods
  private getDefaultData(): HeroPanelData {
    return { ...MinimalHeroPanelComponent.DEFAULT_DATA };
  }

  /**
   * Create and trigger a hidden file input to let the user pick a .png, .jpg, .jpeg, or .mp4 file; validates file size and forwards the file to handleFileUpload if valid.
   * @example
   * createFileInput()
   * // Opens the file picker and, if a selected file passes size validation, calls handleFileUpload(file)
   * @returns {void} Opens the file selector and may initiate file upload for a validated file.
   **/
  private createFileInput(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.mp4';
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && this.validateFileSize(file)) {
        this.handleFileUpload(file);
      }
    };
    input.click();
  }

  /**
   * Handle image file upload: create a temporary preview URL, upload the file via templateService, update component state (preview, uploaded URL, loading/error flags), emit valueChange on success, and handle errors.
   * @example
   * handleFileUpload(new File([''], 'hero.png', { type: 'image/png' }))
   * undefined
   * @param {File} file - The image File object to preview and upload.
   * @returns {void} Does not return a value; updates component state and emits changes.
   **/
  private handleFileUpload(file: File): void {
    // Show loading state
    this.isUploadingImage = true;
    this.uploadError = null;

    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    if (!this.data.image) {
      this.data.image = {
        src: tempPreviewUrl,
        alt: file.name,
        width: '420px',
        height: '320px',
        scaletype: 'fit',
      };
    } else {
      this.data.image.src = tempPreviewUrl;
      if (!this.data.image.alt) {
        this.data.image.alt = file.name;
      }
    }
    this.data.imageUrl = tempPreviewUrl;
    this.data.imageFit = this.data.image.scaletype || 'fit';

    // Upload file to server using template service
    this.templateService.uploadWebsiteFile(file).subscribe({
      next: uploadResponse => {
        this.logger.debug('🖼️ Hero image uploaded successfully:', uploadResponse);

        // Get image URL from response - check multiple possible response structures
        const imageUrl =
          uploadResponse?.data?.url ||
          uploadResponse?.data?.file ||
          uploadResponse?.data?.file_url ||
          uploadResponse?.url ||
          uploadResponse?.file ||
          uploadResponse?.file_url ||
          (uploadResponse?.data && typeof uploadResponse.data === 'string'
            ? uploadResponse.data
            : null);

        if (imageUrl) {
          this.logger.debug('🖼️ Hero image URL extracted:', imageUrl);

          // Replace temporary preview URL with uploaded URL
          if (this.data.image) {
            this.data.image.src = imageUrl;
          }
          this.data.imageUrl = imageUrl;

          // Clean up temporary blob URL
          URL.revokeObjectURL(tempPreviewUrl);

          // Emit change with uploaded URL
          this.valueChange.emit(this.data);
          this.isUploadingImage = false;
        } else {
          this.logger.error(
            '🖼️ Image URL not found in upload response. Full response:',
            uploadResponse
          );
          this.uploadError = 'Failed to upload image. Please try again.';
          this.isUploadingImage = false;
          // Keep the temporary preview for now, but show error
        }
      },
      error: error => {
        this.logger.error('🖼️ Error uploading hero image:', error);
        this.uploadError = 'Failed to upload image. Please try again.';
        this.isUploadingImage = false;
        // Keep the temporary preview for now, but show error
      },
    });
  }

  private validateFileSize(file: File): boolean {
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'warn',
        summary: 'File Too Large',
        detail: 'File size must be less than 5MB',
      });
      return false;
    }
    return true;
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!MinimalHeroPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.showImageFitDropdown = false;
      this.showNavigationDropdown = false;
      this.imageFitDropdownAbove = false;
      this.navigationDropdownAbove = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Recalculate positions when window is resized
    if (this.showImageFitDropdown) {
      this.checkImageFitDropdownPosition();
    }
    if (this.showNavigationDropdown) {
      this.checkNavigationDropdownPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // Recalculate positions when scrolling
    if (this.showImageFitDropdown) {
      this.checkImageFitDropdownPosition();
    }
    if (this.showNavigationDropdown) {
      this.checkNavigationDropdownPosition();
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
